import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';
import { Alert, Badge, Button, EmptyState, Progress, Skeleton } from '../ui';
import SettingsGroup from './SettingsGroup';

const LEVELS = ['new', 'seen', 'practiced', 'secure'];
const LEVEL_TONE = { new: undefined, seen: 'warning', practiced: 'brand', secure: 'success' };

/**
 * Aggregated learning needs (spec §10.6).
 *
 * The only aggregation endpoint that exists today is
 * `GET /api/missions/mastery`, which is scoped to the calling account. The copy
 * therefore states the scope explicitly instead of implying a tenant-wide roll
 * up that the backend cannot yet produce — see `openIssues` in the WP10 report
 * for the missing `/api/admin/learning-needs` endpoint.
 */
export default function LearningNeeds({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setState('loading');
    setError('');
    try {
      const res = await fetch('/api/missions/mastery?days=30', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        setState('denied');
        return;
      }
      if (res.status === 404) {
        setState('nosource');
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      setData(await res.json());
      setState('ready');
    } catch {
      setError(t('states.error'));
      setState('error');
    }
  }, [token, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    return (
      <SettingsGroup title={t('admin.learningNeeds')} description={t('admin.learningNeedsDesc')}>
        <div role="status" aria-live="polite">
          <span className="etch-visually-hidden">{t('states.loading')}</span>
          <Skeleton shape="text" />
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      </SettingsGroup>
    );
  }

  if (state === 'denied') {
    return (
      <SettingsGroup title={t('admin.learningNeeds')}>
        <Alert tone="warning" title={t('states.permissionDenied')}>
          {t('admin.adminOnlyHint')}
        </Alert>
      </SettingsGroup>
    );
  }

  if (state === 'nosource' ) {
    return (
      <SettingsGroup title={t('admin.learningNeeds')} description={t('admin.learningNeedsDesc')}>
        <EmptyState icon={<Gauge size={28} />} title={t('states.noSource')}>
          {t('admin.learningNeedsNoSource')}
        </EmptyState>
      </SettingsGroup>
    );
  }

  if (state === 'error') {
    return (
      <SettingsGroup title={t('admin.learningNeeds')}>
        <Alert tone="danger" title={t('states.error')}>
          {error}
        </Alert>
        <Button variant="secondary" onClick={load}>
          {t('states.retry')}
        </Button>
      </SettingsGroup>
    );
  }

  const rawLevels = Array.isArray(data?.levels) ? data.levels : [];
  const counts = LEVELS.map((level) => {
    const entry = rawLevels.find((l) => (l.level ?? l.id) === level);
    return { level, count: Number(entry?.count ?? entry?.total ?? 0) };
  });
  const total = counts.reduce((sum, c) => sum + c.count, 0);
  const calibration = data?.calibration;

  return (
    <SettingsGroup
      title={t('admin.learningNeeds')}
      description={t('admin.learningNeedsDesc')}
      actions={
        <Button variant="ghost" size="compact" onClick={load}>
          {t('states.retry')}
        </Button>
      }
    >
      {total === 0 ? (
        <EmptyState icon={<Gauge size={28} />} title={t('states.empty')}>
          {t('admin.learningNeedsEmpty')}
        </EmptyState>
      ) : (
        <ul className="etch-stack" data-gap="3" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {counts.map(({ level, count }) => (
            <li key={level}>
              <Progress
                value={count}
                max={total}
                showValue={false}
                label={
                  <span className="etch-row">
                    <Badge tone={LEVEL_TONE[level]}>{t(`mastery.level.${level}`)}</Badge>
                    <span className="etch-meta">{count}</span>
                  </span>
                }
              />
            </li>
          ))}
        </ul>
      )}

      {calibration?.samples > 0 && (
        <p className="etch-meta">
          {t('admin.learningNeedsCalibration', {
            error: Math.round((calibration.error ?? 0) * 100),
            samples: calibration.samples
          })}
        </p>
      )}

      <Alert tone="info" title={t('admin.learningNeedsScopeTitle')}>
        {t('admin.learningNeedsScope')}
      </Alert>
    </SettingsGroup>
  );
}
